import React, { useEffect, useState, useRef, useCallback } from "react";
import DOMPurify from "dompurify";
import { MdWhatshot } from "react-icons/md";
import { FaFilePdf } from "react-icons/fa";
import axios from "axios";
import "./css/quickguides-overrides.css";

export default function QuickGuides({
                                           contextPath,
                                           mode = "profiler",
                                           openSheet,
                                       }) {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const [hotOpen, setHotOpen] = useState(false);
    const [hotLoading, setHotLoading] = useState(false);
    const [hotErr, setHotErr] = useState(null);
    const [hotHtml, setHotHtml] = useState("");

    const [newsCategories, setNewsCategories] = useState([{ id: 0, item: "All News" }]);
    const [newsCategoryID, setNewsCategoryID] = useState(0);
    const [newsKeyword, setNewsKeyword] = useState("");
    const newsContainerRef = useRef(null);

    const [openGlossary, setOpenGlossary] = useState(new Set());

    const toggleGlossary = (idx) => {
        setOpenGlossary(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    const expandAllGlossary = () => {
        if (!Array.isArray(detail?.glossary)) return;
        setOpenGlossary(new Set(detail.glossary.map((_, i) => i)));
    };

    const collapseAllGlossary = () => setOpenGlossary(new Set());

    async function openHotNews() {
        if (!detail?.id) return;
        setHotOpen(true);
        setNewsKeyword(detail.hotNews || "");
        setNewsCategoryID(0);
        await loadNewsCategories();
        await fetchHotNews({
            quickguideId: detail.id,
            hot: detail.hotNews || "",
            categoryID: 0,
        });
    }


    const onNewsClick = useCallback((e) => {
           const a = e.target.closest("a");
           if (!a) return;
           const href = a.getAttribute("href");
           if (!href || href === "#") return;
           e.preventDefault();
           window.open(href, "_blank", "noopener,noreferrer");
         }, []);

        useEffect(() => {
              const el = newsContainerRef.current;
               if (!el) return;
               el.addEventListener("click", onNewsClick);
               return () => el.removeEventListener("click", onNewsClick);
             }, [onNewsClick, hotHtml]);


    async function loadNewsCategories() {
          try {
                const res = await axios.get(API.newsCategories, { headers });
                const items = Array.isArray(res.data) ? res.data : [];
                // normalize to {id, item}
                    const normalized = items.map(x => ({
                      id: Number(x.id ?? x.ID ?? x.categoryId ?? 0),
                      item: String(x.item ?? x.name ?? "Unnamed")
                })).filter(c => !Number.isNaN(c.id));
                setNewsCategories([{ id: 0, item: "All News" }, ...normalized]);
              } catch {
                // keep fallback "All News" if fetch fails
                  }
        }

    async function fetchHotNews({ quickguideId, hot, categoryID }) {
          setHotLoading(true);
          setHotErr(null);
          try {
                const url = API.newsWithFilters({
                      quickguideId,
                      hot,
                      categoryID
                    });
                const res = await axios.get(url, { headers });
                const html = typeof res.data === "string" ? res.data : (res.data?.html ?? "");
                setHotHtml(html);
              } catch (e) {
                setHotErr(e);
              } finally {
                setHotLoading(false);
               }
        }

    const getFilenameFromDisposition = (value) => {
        if (!value) return "";
        const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match?.[1]) {
            try {
                return decodeURIComponent(utf8Match[1]);
            } catch {
                return utf8Match[1];
            }
        }
        const plainMatch = value.match(/filename="?([^"]+)"?/i);
        return plainMatch?.[1] || "";
    };

    async function downloadRegulationProfilePdf() {
        if (!detail?.regulationId) return;
        try {
            const res = await axios.get(API.regulationPdf(detail.regulationId), {
                headers,
                responseType: "blob",
            });
            const filename =
                getFilenameFromDisposition(res.headers["content-disposition"]) ||
                `${detail.name || "regulation-profile"}.pdf`;
            const blob = new Blob([res.data], { type: "application/pdf" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
        } catch (e) {
            console.error("Regulation profile export failed:", e);
            window.alert("Could not download the regulation profile PDF.");
        }
    }


    const API = {
        headers: `${contextPath}/quickguides/headers?mode=${encodeURIComponent(mode)}`,
        subheaders: (headerId) =>
            `${contextPath}/quickguides/subheaders?headerId=${headerId}&mode=${encodeURIComponent(mode)}`,
        guides: (subheaderId) =>
            `${contextPath}/quickguides?subheaderId=${subheaderId}&mode=${encodeURIComponent(mode)}`,
        guideDetail: (id) =>
            `${contextPath}/quickguides/${id}?mode=${encodeURIComponent(mode)}`,
        regulationWord: (id) => `${contextPath}/OpenRegulationprofile?id=${id}`,
        regulationPdf: (id) => `${contextPath}/exports/regulation-profiles/${id}`,
        hotNews: (id, hot) =>
            `${contextPath}/news?quickguideId=${id}&hot=${encodeURIComponent(hot || "")}&mode=${encodeURIComponent(mode)}`,
        newsWithFilters: ({ quickguideId, hot, categoryID }) =>
            `${contextPath}/news?quickguideId=${quickguideId}` +
            `&hot=${encodeURIComponent(hot || "")}` +
            `&categoryID=${categoryID ?? 0}` +
            `&mode=${encodeURIComponent(mode)}`,
        newsCategories: `${contextPath}/news/categories`,
    };

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [headersList, setHeadersList] = useState([]);
    const [subheadersMap, setSubheadersMap] = useState({});
    const [guidesMap, setGuidesMap] = useState({});

    const [activeHeaderId, setActiveHeaderId] = useState(null);
    const [activeSubheaderId, setActiveSubheaderId] = useState(null);
    const [activeGuideId, setActiveGuideId] = useState(null);

    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Load headers
    useEffect(() => {
        let alive = true;
        setLoading(true);
        setErr(null);
        axios
            .get(API.headers, { headers })
            .then((res) => {
                if (!alive) return;
                const list = res.data || [];
                setHeadersList(list);
                setActiveHeaderId(list[0]?.id ?? null);
            })
            .catch(setErr)
            .finally(() => alive && setLoading(false));
        return () => (alive = false);
    }, []); // eslint-disable-line

    // Load subheaders for active header
    useEffect(() => {
        if (!activeHeaderId) return;
        if (subheadersMap[activeHeaderId]) {
            setActiveSubheaderId(subheadersMap[activeHeaderId][0]?.id ?? null);
            return;
        }
        let alive = true;
        axios
            .get(API.subheaders(activeHeaderId), { headers })
            .then((res) => {
                if (!alive) return;
                const list = res.data || [];
                setSubheadersMap((p) => ({ ...p, [activeHeaderId]: list }));
                setActiveSubheaderId(list[0]?.id ?? null);
            })
            .catch(setErr);
        return () => (alive = false);
    }, [activeHeaderId]); // eslint-disable-line

    // Load guides for active subheader
    useEffect(() => {
        if (!activeSubheaderId) return;
        if (guidesMap[activeSubheaderId]) {
            setActiveGuideId(guidesMap[activeSubheaderId][0]?.id ?? null);
            return;
        }
        let alive = true;
        axios
            .get(API.guides(activeSubheaderId), { headers })
            .then((res) => {
                if (!alive) return;
                const list = res.data || [];
                setGuidesMap((p) => ({ ...p, [activeSubheaderId]: list }));
                setActiveGuideId(list[0]?.id ?? null);
            })
            .catch(setErr);
        return () => (alive = false);
    }, [activeSubheaderId]); // eslint-disable-line

    // Load detail for active guide
    useEffect(() => {
        if (!activeGuideId) {
            setDetail(null);
            return;
        }
        let alive = true;
        setDetailLoading(true);
        axios
            .get(API.guideDetail(activeGuideId), { headers })
            .then((res) => {
                if (!alive) return;
                setDetail(res.data || null);
            })
            .catch(setErr)
            .finally(() => alive && setDetailLoading(false));
        return () => (alive = false);
    }, [activeGuideId]); // eslint-disable-line

    useEffect(() => {
        if (!hotOpen) return;
        const onKey = (e) => e.key === "Escape" && setHotOpen(false);
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [hotOpen]);


    const headerSubs = subheadersMap[activeHeaderId] || [];
    const subGuides = guidesMap[activeSubheaderId] || [];
    const hasRegulationPdf = Boolean(detail?.regulationId);

    return (
        <div className="flex flex-col gap-3">
            {/* Headers row */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                {loading ? (
                    <div className="text-gray-500 italic">Loading headers…</div>
                ) : err ? (
                    <div className="text-red-600">Failed to load Quick Guides.</div>
                ) : headersList.length === 0 ? (
                    <div className="text-gray-500 italic">No Quick Guides available.</div>
                ) : (
                    headersList.map((h) => (
                        <button
                            key={h.id}
                            className={`px-3 py-1.5 rounded-full border text-sm transition""
                ${
                                h.id === activeHeaderId
                                    ? "border-[#25408f] text-[#25408f] font-semibold"
                                    : "border-gray-300 hover:border-gray-400"
                            }`}
                            onClick={() => setActiveHeaderId(h.id)}
                            title={h.name}
                        >
                            {h.name}
                        </button>
                    ))
                )}
            </div>

            {/* Subheaders row */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                {headerSubs.length === 0 ? (
                    <div className="text-gray-500 italic">No sections.</div>
                ) : (
                    headerSubs.map((s) => (
                        <button
                            key={s.id}
                            className={`px-2.5 py-1 rounded-full border text-xs transition
                ${
                                s.id === activeSubheaderId
                                    ? "border-[#25408f] text-[#25408f] font-semibold"
                                    : "border-gray-300 hover:border-gray-400"
                            }`}
                            onClick={() => setActiveSubheaderId(s.id)}
                            title={s.name}
                        >
                            {s.name}
                        </button>
                    ))
                )}
            </div>

            {subGuides.length === 0 ? (
                <div className="text-gray-500 italic">No topics in this section.</div>
            ) : (
                <div className="relative group">
                    <select
                        id="topicsSelect"
                        className="w-full appearance-none rounded-xl border border-gray-300 bg-white
                 py-2.5 pl-3 pr-10 text-sm text-gray-900 shadow-sm transition
                 hover:border-gray-400
                 focus:outline-none focus:border-[#25408f] focus:ring-4 focus:ring-[#25408f]/20
                 disabled:opacity-50 disabled:cursor-not-allowed"
                        value={activeGuideId ?? ""}
                        onChange={(e) => setActiveGuideId(Number(e.target.value))}
                    >
                        {subGuides.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.name}
                            </option>
                        ))}
                    </select>

                    {/* Chevron */}
                    <svg
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5
                 text-gray-500 group-hover:text-gray-600"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                            clipRule="evenodd"
                        />
                    </svg>
                </div>
            )}


            <div className="grid grid-cols-[280px_1fr] gap-4 min-h-[280px] h-[550px] overflow-hidden">
             <div className="col-span-2 min-h-0 border border-gray-200 rounded-xl p-4 overflow-y-auto">
                    {!activeGuideId ? (
                        <div className="text-gray-500 italic">Select a topic…</div>
                    ) : detailLoading ? (
                        <div className="text-gray-500 italic">Loading details…</div>
                    ) : !detail ? (
                        <div className="text-red-600">No details found.</div>
                    ) : (
                        <>
                            <div className="mb-4 flex items-start justify-between gap-3 border-b pb-3">
                                <h2 className="text-xl font-semibold text-gray-900">{detail.name}</h2>
                                {hasRegulationPdf ? (
                                    <button
                                        type="button"
                                        onClick={downloadRegulationProfilePdf}
                                        className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 transition hover:bg-red-100"
                                        title="Download Regulation profile PDF"
                                    >
                                        <FaFilePdf className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                ) : null}
                            </div>
                            {/* Hot News */}
                            {/* Products (header + Hot News link on the right) */}
                            {Array.isArray(detail.products) && detail.products.length > 0 && (
                              <>
                                <div className="mt-2 mb-2 flex items-center justify-between border-b pb-2">
                                      <div className="textHeadings text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <span>Products</span>
                                      </div>
                                    <button
                                      type="button"
                                      onClick={openHotNews}
                                      className="inline-flex items-center gap-2 whitespace-nowrap text-sm font-bold
                                                text-[var(--rusty-orange)] hover:opacity-80
                                                 focus:outline-none focus:ring-2 focus:ring-[var(--rusty-orange)]/30 rounded"
                                      title="Open related Hot News"
                                    >
                                      <MdWhatshot className="h-4 w-4 -mt-px" aria-hidden="true" />
                                      <span>Hot News</span>
                                    </button>
                                </div>
                                <ul className="space-y-1 mb-3">
                                      {detail.products.map((p) => (
                                        <li key={p.id} className="flex items-center gap-2">
                                              <button
                                                className="text-[#25408f] hover:opacity-80 transition"
                                                onClick={() => openSheet?.("product", p.id, p.name || `Product #${p.id}`)}  
                                              title="Open product tearsheet"
                                              >
                                                {p.name}
                                              </button>
                                        </li>
                                      ))}
                                    </ul>
                              </>
                            )}
                            {/* Vendors */}
                            {Array.isArray(detail.vendors) && detail.vendors.length > 0 && (
                                  <>
                                        <div className="mt-4 mb-2 flex items-center justify-between border-b pb-2">
                                          <div className="textHeadings text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <span>Vendors</span>
                                          </div>
                                        </div>
                                        <ul className="space-y-1 mb-3">
                                          {detail.vendors.map((v) => (
                                            <li key={v.id} className="flex items-center gap-2">
                                                  <button
                                                    className="text-[#25408f] hover:opacity-80 transition"
                                                    onClick={() => openSheet?.("vendor", v.id, v.name || `Vendor #${v.id}`)}
                                                    title="Open vendor tearsheet"
                                                  >
                                                    {v.name}
                                                  </button>
                                            </li>
                                          ))}
                                        </ul>
                                      </>
                            )}

                            {/* Links */}
                            {Array.isArray(detail.links) && detail.links.length > 0 && (
                                <>
                                    <div className="mt-4 mb-2 flex items-center justify-between border-b pb-2">
                                        <div className="textHeadings text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <span>Links</span>
                                        </div>
                                    </div>
                                    <ul className="links-list not-prose list-disc list-inside space-y-1 mb-3">
                                        {detail.links.map((l, i) => (
                                            <li key={i}>
                                                <a
                                                    href={l.url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-[#25408f] hover:opacity-80 transition no-underline"
                                                    style={{ textDecoration: "none", borderBottom: "0", backgroundImage: "none" }}
                                                >
                                                    {l.name || l.url}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}

                            {/* Glossary */}
                            {Array.isArray(detail.glossary) && detail.glossary.length > 0 && (
                                <>
                                    <div className="mt-4 mb-2 flex items-center justify-between border-b pb-2">
                                        <div className="textHeadings text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            <span>Definitions / Glossary</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={expandAllGlossary}
                                                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                                                title="Expand all"
                                            >
                                                Expand all
                                            </button>
                                            <button
                                                type="button"
                                                onClick={collapseAllGlossary}
                                                className="text-xs px-2 py-1 rounded border border-gray-300 hover:bg-gray-50"
                                                title="Collapse all"
                                            >
                                                Collapse all
                                            </button>
                                        </div>
                                    </div>

                                    <ul className="divide-y rounded-lg border border-gray-200 bg-white">
                                        {detail.glossary.map((g, i) => {
                                            const open = openGlossary.has(i);
                                            return (
                                                <li key={i} className="p-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleGlossary(i)}
                                                        aria-expanded={open}
                                                        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#25408f]/30 rounded-t-lg"
                                                        title={g.definition || ""}
                                                    >
              <span className="text-sm font-medium text-[#e87722]">
                {g.term}
              </span>
                                                        <svg
                                                            className={`h-5 w-5 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                            aria-hidden="true"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>

                                                    {open && (
                                                        <div className="px-3 pb-3 text-sm text-gray-700">
                                                            {g.definition ? g.definition : <em className="text-gray-500">No definition provided.</em>}
                                                        </div>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </>
                            )}

                        </>
                    )}
                </div>
            </div>

            {hotOpen && (
                <div className="fixed inset-0 z-[9999]">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setHotOpen(false)}
                        aria-hidden="true"
                    />
                    {/* Modal */}
                    <div className="relative z-10 mx-auto mt-10 w-[min(92vw,900px)] max-h-[80vh] overflow-hidden rounded-2xl bg-white shadow-xl">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b px-4 py-3">
                            <div className="inline-flex items-center gap-2 text-gray-900 font-semibold">
                                <MdWhatshot className="h-5 w-5 text-[var(--rusty-orange)]" aria-hidden="true" />
                                <span>Hot News</span>
                            </div>
                            <button
                                type="button"
                                onClick={() => setHotOpen(false)}
                                className="rounded px-2 py-1 text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#25408f]/30"
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>
                        {/* Body */}
<div className="p-4 space-y-3 overflow-y-auto max-h-[calc(80vh-56px)]">
  {/* Controls (Category + Keyword + Search/Clear) */}
    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
        <div className="min-w-[200px]">
            <label htmlFor="newsCategory" className="block text-xs font-semibold text-gray-700 mb-1">
                Category
            </label>
            <select
                id="newsCategory"
                value={newsCategoryID}
                onChange={(e) => setNewsCategoryID(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                 focus:outline-none focus:ring-4 focus:ring-[#25408f]/20 focus:border-[#25408f]"
            >
                {newsCategories.map(c => (
                    <option key={c.id} value={c.id}>{c.item}</option>
                ))}
            </select>
        </div>

        <div className="min-w-[220px]">
            <label htmlFor="newsKeyword" className="block text-xs font-semibold text-gray-700 mb-1">
                Keyword
            </label>
            <input
                id="newsKeyword"
                type="text"
                value={newsKeyword}
                onChange={(e) => setNewsKeyword(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        fetchHotNews({ quickguideId: detail.id, hot: newsKeyword, categoryID: newsCategoryID });
                    }
                }}
                placeholder="-- Keyword Search --"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm
                 focus:outline-none focus:ring-4 focus:ring-[#25408f]/20 focus:border-[#25408f]"
            />
        </div>

        <div className="flex gap-2 sm:justify-end">
            <button
                type="button"
                onClick={() => fetchHotNews({ quickguideId: detail.id, hot: newsKeyword, categoryID: newsCategoryID })}
                className="rounded-lg bg-[var(--rusty-orange)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
                Search
            </button>
            <button
                type="button"
                onClick={() => {
                    setNewsKeyword("");
                    setNewsCategoryID(0);
                    fetchHotNews({ quickguideId: detail.id, hot: "", categoryID: 0 });
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
                Clear
            </button>
        </div>
    </div>

  {/* Results */}
  {hotLoading && <div className="text-gray-500 italic">Loading…</div>}
  {hotErr && <div className="text-red-600 text-sm">Failed to load Hot News.</div>}
  {!hotLoading && !hotErr && (
    <div
      ref={newsContainerRef}
      className="prose max-w-none prose-headings:mt-4 prose-a:text-[#25408f] hover:prose-a:opacity-80"
      // server returns HTML; we sanitize for safety
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(hotHtml) }}
    />
  )}
</div>
                    </div>
                </div>
            )}

        </div>

    );
}
